import { useSeoMeta } from '@unhead/react';
import { useParams, Link } from 'react-router-dom';
import { nip19 } from 'nostr-tools';
import {
  ArrowLeft,
  Plus,
  Check,
  Loader2,
  Copy,
  ExternalLink,
  User,
  Calendar,
  Hash,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { Skeleton } from '@/components/ui/skeleton';
import { AppLayout } from '@/components/AppLayout';
import { useEmojiPack } from '@/hooks/useEmojiPacks';
import { useEmojiListManager } from '@/hooks/useEmojiListManager';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { useAuthor } from '@/hooks/useAuthor';
import { genUserName } from '@/lib/genUserName';
import { useToast } from '@/hooks/useToast';
import { useState } from 'react';
import NotFound from './NotFound';
import { cn } from '@/lib/utils';

export default function PackDetailPage() {
  const { nip19: naddr } = useParams<{ nip19: string }>();
  const { toast } = useToast();
  const { user } = useCurrentUser();
  const { isPackAdded, addPack, removePack } = useEmojiListManager();
  const [toggling, setToggling] = useState(false);
  const [copiedCode, setCopiedCode] = useState<string | null>(null);

  let pubkey = '';
  let dTag = '';

  try {
    const decoded = nip19.decode(naddr!);
    if (decoded.type === 'naddr' && decoded.data.kind === 30030) {
      pubkey = decoded.data.pubkey;
      dTag = decoded.data.identifier;
    }
  } catch {
    // handled below
  }

  const { data: pack, isLoading } = useEmojiPack(pubkey, dTag);
  const author = useAuthor(pubkey);
  const displayName = author.data?.metadata?.name ?? genUserName(pubkey);
  const avatar = author.data?.metadata?.picture;

  const pageTitle = pack ? `${pack.title} — EmojiVerse` : 'Emoji Pack — EmojiVerse';
  const pageDescription = pack
    ? (pack.description || `${pack.emojis.length} custom emoji${pack.emojis.length !== 1 ? 's' : ''} by ${displayName}`)
    : 'A custom emoji pack on Nostr.';
  // Use the pack's cover image if available, otherwise fall back to the site OG image
  const pageImage = pack?.image || 'https://emojiverse.shakespeare.wtf/og-image.jpg';
  const pageUrl = `https://emojiverse.shakespeare.wtf/${naddr}`;

  useSeoMeta({
    title: pageTitle,
    description: pageDescription,
    ogType: 'website',
    ogSiteName: 'EmojiVerse',
    ogTitle: pageTitle,
    ogDescription: pageDescription,
    ogUrl: pageUrl,
    ogImage: pageImage,
    ogImageAlt: pageTitle,
    twitterCard: 'summary_large_image',
    twitterTitle: pageTitle,
    twitterDescription: pageDescription,
    twitterImage: pageImage,
    twitterImageAlt: pageTitle,
  });

  if (!pubkey || !dTag) return <NotFound />;

  const copyShortcode = (shortcode: string) => {
    navigator.clipboard.writeText(`:${shortcode}:`);
    setCopiedCode(shortcode);
    setTimeout(() => setCopiedCode(null), 1500);
  };

  const handleToggle = async () => {
    if (!pack) return;
    setToggling(true);
    try {
      if (isPackAdded(pack)) {
        await removePack(pack);
      } else {
        await addPack(pack);
      }
    } finally {
      setToggling(false);
    }
  };

  const added = pack ? isPackAdded(pack) : false;

  return (
    <AppLayout>
      {/* Back */}
      <div className="mb-6">
        <Link
          to="/"
          className="inline-flex items-center gap-2 text-white/40 hover:text-white/70 text-sm transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Browse
        </Link>
      </div>

      {isLoading ? (
        <div className="space-y-6">
          <div className="flex items-start gap-6">
            <Skeleton className="w-24 h-24 rounded-2xl bg-white/10" />
            <div className="flex-1 space-y-3">
              <Skeleton className="h-8 w-64 bg-white/10" />
              <Skeleton className="h-4 w-48 bg-white/10" />
              <Skeleton className="h-10 w-32 bg-white/10 rounded-full" />
            </div>
          </div>
          <div className="grid grid-cols-4 sm:grid-cols-6 md:grid-cols-8 lg:grid-cols-10 gap-3">
            {Array.from({ length: 20 }).map((_, i) => (
              <Skeleton key={i} className="aspect-square rounded-xl bg-white/10" />
            ))}
          </div>
        </div>
      ) : !pack ? (
        <div className="text-center py-20">
          <div className="text-6xl mb-4">🔍</div>
          <p className="text-white/50 text-lg">Emoji pack not found</p>
          <Link to="/" className="mt-4 inline-block text-violet-400 hover:text-violet-300 text-sm underline underline-offset-4">
            Browse all packs
          </Link>
        </div>
      ) : (
        <>
          {/* Pack header */}
          <div className="flex flex-col sm:flex-row items-start gap-6 mb-10">
            {/* Cover / emoji preview */}
            <div className="w-24 h-24 rounded-2xl bg-white/10 border border-white/10 overflow-hidden flex items-center justify-center shrink-0">
              {pack.image ? (
                <img src={pack.image} alt={pack.title} className="w-full h-full object-cover" />
              ) : pack.emojis[0] ? (
                <img src={pack.emojis[0].url} alt={pack.emojis[0].shortcode} className="w-14 h-14 object-contain" />
              ) : (
                <span className="text-4xl">✨</span>
              )}
            </div>

            <div className="flex-1 min-w-0">
              <h1 className="text-3xl font-bold text-white mb-2">{pack.title}</h1>
              {pack.description && (
                <p className="text-white/50 mb-3">{pack.description}</p>
              )}

              <div className="flex flex-wrap items-center gap-3 mb-4">
                {/* Author */}
                <Link
                  to={`/${nip19.npubEncode(pubkey)}`}
                  className="flex items-center gap-2 hover:text-white/80 transition-colors"
                >
                  {avatar ? (
                    <img src={avatar} alt={displayName} className="w-5 h-5 rounded-full object-cover" />
                  ) : (
                    <User className="w-4 h-4 text-white/40" />
                  )}
                  <span className="text-sm text-white/50">{displayName}</span>
                </Link>

                <span className="text-white/20">·</span>
                <div className="flex items-center gap-1.5 text-sm text-white/40">
                  <Hash className="w-3.5 h-3.5" />
                  {pack.dTag}
                </div>
                <span className="text-white/20">·</span>
                <div className="flex items-center gap-1.5 text-sm text-white/40">
                  <Calendar className="w-3.5 h-3.5" />
                  {new Date(pack.createdAt * 1000).toLocaleDateString()}
                </div>
              </div>

              <div className="flex items-center gap-3">
                <Badge className="bg-white/10 text-white/60 border-0">
                  {pack.emojis.length} emojis
                </Badge>

                {user && (
                  <Button
                    onClick={handleToggle}
                    disabled={toggling}
                    className={cn(
                      'rounded-full gap-2 transition-all',
                      added
                        ? 'bg-white/10 text-white/70 hover:bg-red-500/20 hover:text-red-300'
                        : 'bg-violet-500 hover:bg-violet-400 text-white shadow-lg shadow-violet-500/30',
                    )}
                  >
                    {toggling ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : added ? (
                      <><Check className="w-4 h-4" />Remove Pack</>
                    ) : (
                      <><Plus className="w-4 h-4" />Add to Profile</>
                    )}
                  </Button>
                )}

                <a
                  href={`https://njump.me/${naddr}`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <Button variant="ghost" size="sm" className="text-white/40 hover:text-white/70 hover:bg-white/10 rounded-full gap-1.5">
                    <ExternalLink className="w-3.5 h-3.5" />
                    njump
                  </Button>
                </a>
              </div>
            </div>
          </div>

          {/* Emoji grid */}
          <div>
            <h2 className="text-base font-semibold text-white/70 mb-4">All Emojis</h2>
            <div className="grid grid-cols-4 sm:grid-cols-6 md:grid-cols-8 lg:grid-cols-10 gap-3">
              {pack.emojis.map((emoji) => (
                <Tooltip key={emoji.shortcode}>
                  <TooltipTrigger asChild>
                    <button
                      onClick={() => copyShortcode(emoji.shortcode)}
                      className={cn(
                        'aspect-square rounded-xl border transition-all duration-200 flex items-center justify-center p-2.5 group',
                        copiedCode === emoji.shortcode
                          ? 'bg-violet-500/20 border-violet-500/40'
                          : 'bg-white/5 border-white/10 hover:bg-white/10 hover:border-white/20',
                      )}
                    >
                      <img
                        src={emoji.url}
                        alt={emoji.shortcode}
                        className="w-full h-full object-contain group-hover:scale-110 transition-transform duration-200"
                        loading="lazy"
                        onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                      />
                    </button>
                  </TooltipTrigger>
                  <TooltipContent className="bg-gray-900 border-white/10 text-white text-xs">
                    <div className="flex items-center gap-1.5">
                      {copiedCode === emoji.shortcode ? (
                        <><Check className="w-3 h-3 text-green-400" />Copied!</>
                      ) : (
                        <><Copy className="w-3 h-3 text-white/40" />:{emoji.shortcode}:</>
                      )}
                    </div>
                  </TooltipContent>
                </Tooltip>
              ))}
            </div>
            <p className="text-white/30 text-xs mt-4 text-center">
              Click any emoji to copy its shortcode
            </p>
          </div>
        </>
      )}
    </AppLayout>
  );
}
